import Component from '@glimmer/component';
import { guidFor } from '@ember/object/internals';
import { htmlSafe } from '@ember/template';
import Heading from './heading';
import IconHeading from './icon-heading';
import Text from './text';
import type { ComponentLike } from '@glint/template';

// ---------------------------------------------------------------------------
// PageHero – gradient hero banner component
// ---------------------------------------------------------------------------

/**
 * Gradient preset for the hero background.
 *
 * The original API exposed an open palette of decorative Tailwind multi-stop
 * gradients (violet / emerald / amber / teal / gray / indigo / sky / blue /
 * rose / red / fuchsia). Per the 2026-05-27 product decision decorative hero
 * gradients now collapse onto a **small curated brand-leaning set** routed
 * through TIO tokens — `brand` is the canonical purple presentation, the
 * others are intentional brand-adjacent variations for visual variety.
 * Dark mode follows the tokens automatically.
 *
 *  - `brand`      purple → deeper purple (default; the canonical brand hero)
 *  - `brand-soft` pale purple wash for hero surfaces that host dark on-brand
 *                 content instead of the usual white-on-gradient (used by the
 *                 TAS dashboards that wrap a brand-tinted summary panel)
 *  - `warm`       purple → red brand-adjacent
 *  - `cool`       teal → purple brand-adjacent
 *  - `neutral`    warm-grey neutrals (subdued)
 *  - `hero`       Figma multi-glow gradient on dark charcoal (the "premium"
 *                 pick for landing/marketing surfaces)
 */
export type PageHeroGradient = 'brand' | 'brand-soft' | 'warm' | 'cool' | 'neutral' | 'hero';

/**
 * Corner rounding preset type.
 */
export type PageHeroRounded = 'lg' | 'xl' | '2xl' | '3xl';

/**
 * Decorative background pattern type.
 */
export type PageHeroDecoration = 'none' | 'circles' | 'dots' | 'confetti' | 'smiley' | 'orbs';

export interface PageHeroSignature {
  Args: {
    /** Gradient preset (default: 'brand'). */
    gradient?: PageHeroGradient;
    /** Corner-radius preset (default: the canonical 24px hero radius). */
    rounded?: PageHeroRounded;
    /** Decorative background pattern (default: 'none') */
    decoration?: PageHeroDecoration;
    /**
     * Enables a slow, looping gradient-position animation that makes the background
     * feel alive. Works best with multi-stop gradients. Each PageHero instance gets
     * its own uniquely scoped `@keyframes` so multiple animated heroes on the same
     * page don't conflict.
     */
    animated?: boolean;
    /** Center-align icon, text, and badge blocks (block mode only; has no effect in convenience mode) */
    centered?: boolean;
    /** Convenience: title text (renders Heading in icon area) */
    title?: string;
    /** Convenience: subtitle text below the title */
    subtitle?: string;
    /** Convenience: Phosphor icon component to display beside the title */
    icon?: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Convenience: heading level for the title (default: 1) */
    headingLevel?: 1 | 2 | 3;
  };
  Blocks: {
    default: [];
    icon?: [];
    badge?: [];
    footer?: [];
    /** Optional stat display (right side, requires @title convenience mode) */
    stat?: [];
    /** Optional action buttons below subtitle (requires @title convenience mode) */
    actions?: [];
    /** Optional custom content below title area (requires @title convenience mode) */
    content?: [];
  };
  Element: HTMLDivElement;
}

/**
 * A gradient hero banner with optional named blocks for icon, badge, body,
 * and footer content. Background uses a small set of brand-leaning gradient
 * presets routed through TIO tokens (so dark mode follows the canonical
 * palette automatically). Supports decorative SVG background patterns and
 * flexible semantic rounding. PageHero owns its 24px internal padding and
 * canonical 24px radius; surrounding layout containers own external spacing.
 *
 * **Convenience mode** — use `@title`/`@subtitle`/`@icon` args for HeroBanner-style layout:
 *
 * @example
 * ```gts
 * import PageHero from 'tio-ui/components/page-hero';
 * import { PhPiggyBank } from 'ember-phosphor-icons';
 *
 * <PageHero
 *   @icon={{PhPiggyBank}}
 *   @title="Section 127 Optimizer"
 *   @subtitle="Maximize your tax-free education benefit"
 *   @gradient="cool"
 * >
 *   <:stat>
 *     <div class="tio-text-4xl-bold">$5,250</div>
 *   </:stat>
 *   <:actions>
 *     <Button>Take Action</Button>
 *   </:actions>
 * </PageHero>
 * ```
 *
 * **Block mode** — use named blocks for structured layout:
 *
 * @example
 * ```gts
 * <PageHero @gradient="brand" @decoration="circles">
 *   <:icon>
 *     <PhGraduationCap @size={{32}} />
 *     <Heading @level={{1}} class="tio-fg-inverse">Page Title</Heading>
 *   </:icon>
 *   <:default>
 *     <p>Hero body content goes here.</p>
 *   </:default>
 *   <:footer>
 *     <div class="grid grid-cols-3 gap-4">Stats go here</div>
 *   </:footer>
 * </PageHero>
 * ```
 */
export default class PageHero extends Component<PageHeroSignature> {
  /**
   * Returns the semantic gradient class — `.tio-hero--<preset>` defined in
   * `addons/tio-ui/src/styles/`. Each preset references mode-aware
   * brand/neutral tokens so dark mode cascades for free; the consumer just
   * picks the brand-leaning intent.
   */
  get gradientClass() {
    switch (this.args.gradient) {
      case 'brand-soft':
        return 'tio-hero--brand-soft';
      case 'warm':
        return 'tio-hero--warm';
      case 'cool':
        return 'tio-hero--cool';
      case 'neutral':
        return 'tio-hero--neutral';
      case 'hero':
        return 'tio-hero--hero';
      case 'brand':
      default:
        return 'tio-hero--brand';
    }
  }

  /** Returns the semantic corner-radius modifier for an explicit preset. */
  get roundedClass() {
    switch (this.args.rounded) {
      case 'lg':
        return 'tio-hero--rounded-lg';
      case 'xl':
        return 'tio-hero--rounded-xl';
      case '2xl':
        return 'tio-hero--rounded-2xl';
      case '3xl':
        return 'tio-hero--rounded-3xl';
      default:
        return '';
    }
  }

  get showCircles() {
    return this.args.decoration === 'circles';
  }

  get showDots() {
    return this.args.decoration === 'dots';
  }

  get showConfetti() {
    return this.args.decoration === 'confetti';
  }

  get showSmiley() {
    return this.args.decoration === 'smiley';
  }

  get showOrbs() {
    return this.args.decoration === 'orbs';
  }

  /**
   * A per-instance key used to scope `@keyframes` and the CSS attribute selector
   * so multiple animated heroes on the same page don't share animation state.
   */
  get animKey() {
    return `ph-${guidFor(this)}`;
  }

  /**
   * Returns an injected `<style>` block (as a SafeString) containing the
   * gradient-shift keyframe and the attribute-scoped rule. This keeps the CSS
   * completely self-contained per-instance without requiring Tailwind JIT
   * configuration or a separate stylesheet.
   */
  get animatedGradientHtml() {
    if (!this.args.animated) return htmlSafe('');
    const k = this.animKey;
    return htmlSafe(
      `<style>@keyframes ${k}-shift{0%,100%{background-position:0% 60%}50%{background-position:100% 40%}}[data-hero-anim="${k}"]{background-size:400% 400%;animation:${k}-shift 18s ease infinite;}</style>`
    );
  }

  get isCentered() {
    return this.args.centered === true;
  }

  /** Whether the component is in convenience mode (title arg provided). */
  get hasConvenienceTitle() {
    return !!this.args.title;
  }

  get headingLevel() {
    return this.args.headingLevel ?? 1;
  }

  /** Unique suffix for SVG pattern IDs to avoid collisions with multiple instances. */
  get patternId() {
    return `hero-${guidFor(this)}`;
  }

  <template>
    {{this.animatedGradientHtml}}
    <div
      data-test-page-hero
      data-hero-anim={{if @animated this.animKey}}
      class="tio-hero {{this.gradientClass}} {{this.roundedClass}}"
      ...attributes
    >
      {{! Decorative background patterns }}
      {{#if this.showCircles}}
        <div class="tio-hero-orb tio-hero-orb--top-right"></div>
        <div class="tio-hero-orb tio-hero-orb--bottom-left"></div>
      {{else if this.showDots}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-dots"
                width="10"
                height="10"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-dots)" />
          </svg>
        </div>
      {{else if this.showConfetti}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-confetti"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <circle cx="5" cy="5" r="2" fill="white" />
                <circle cx="15" cy="15" r="1.5" fill="white" />
                <circle cx="10" cy="18" r="1" fill="white" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-confetti)" />
          </svg>
        </div>
      {{else if this.showSmiley}}
        <div class="absolute inset-0 opacity-10" aria-hidden="true">
          <svg class="w-full h-full">
            <defs>
              <pattern
                id="{{this.patternId}}-smiley"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                {{! Face }}
                <circle cx="20" cy="20" r="12" fill="none" stroke="white" stroke-width="1.5" />
                {{! Eyes }}
                <circle cx="15" cy="17" r="1.5" fill="white" />
                <circle cx="25" cy="17" r="1.5" fill="white" />
                {{! Smile }}
                <path
                  d="M14 23 Q20 28 26 23"
                  fill="none"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-linecap="round"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#{{this.patternId}}-smiley)" />
          </svg>
        </div>
      {{else if this.showOrbs}}
        {{!
          Aurora-style animated orbs: three softly blurred circles that drift
          at different speeds using SVG-native <animateTransform>. No CSS or
          Tailwind keyframes required — works entirely inside the SVG element.
        }}
        <div class="absolute inset-0" aria-hidden="true">
          <svg
            class="w-full h-full"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <filter id="{{this.patternId}}-orb-blur" x="-80%" y="-80%" width="260%" height="260%">
                <feGaussianBlur stdDeviation="48" result="blur" />
              </filter>
            </defs>
            {{! Orb 1 – large, anchored top-right, drifts south-west then back }}
            <circle
              cx="88%"
              cy="-10%"
              r="200"
              fill="white"
              fill-opacity="0.14"
              filter="url(#{{this.patternId}}-orb-blur)"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; -65,70; 35,30; 0,0"
                dur="22s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.37 0 0.63 1;0.37 0 0.63 1;0.37 0 0.63 1"
              />
            </circle>
            {{! Orb 2 – medium, anchored bottom-left, drifts north-east then back }}
            <circle
              cx="-5%"
              cy="115%"
              r="165"
              fill="white"
              fill-opacity="0.10"
              filter="url(#{{this.patternId}}-orb-blur)"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 75,-55; -30,25; 0,0"
                dur="28s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.37 0 0.63 1;0.37 0 0.63 1;0.37 0 0.63 1"
              />
            </circle>
            {{! Orb 3 – small, centre, slow figure-eight-ish drift }}
            <circle
              cx="50%"
              cy="55%"
              r="110"
              fill="white"
              fill-opacity="0.07"
              filter="url(#{{this.patternId}}-orb-blur)"
            >
              <animateTransform
                attributeName="transform"
                type="translate"
                values="0,0; 40,-35; -25,40; 0,0"
                dur="34s"
                repeatCount="indefinite"
                calcMode="spline"
                keySplines="0.37 0 0.63 1;0.37 0 0.63 1;0.37 0 0.63 1"
              />
            </circle>
          </svg>
        </div>
      {{/if}}

      {{! Content (z-10 keeps it above decorations) }}
      <div class="relative z-10">
        {{#if (has-block "icon")}}
          {{! Block mode: explicit icon block }}
          <div
            data-test-page-hero-icon
            class="tio-row mb-3 {{if this.isCentered 'justify-center'}}"
          >
            {{yield to="icon"}}
          </div>
          {{yield}}
        {{else if this.hasConvenienceTitle}}
          {{! Convenience mode: @title/@subtitle/@icon args with optional stat/actions }}
          <div class="tio-row-start-between">
            <div class="flex-1">
              <IconHeading @icon={{@icon}} data-test-page-hero-icon class="mb-2">
                <Heading
                  data-test-page-hero-title
                  @level={{this.headingLevel}}
                  @role="page-title"
                  class="tio-hero-title"
                >
                  {{@title}}
                </Heading>
              </IconHeading>
              {{#if @subtitle}}
                {{! `@variant="inherit"` is critical: the default `variant="body"` adds
                    `.tio-text--body`, which would repaint the subtitle instead of
                    letting the parent `.tio-hero` recipe own the color via
                    --tio-hero-fg. }}
                <Text
                  data-test-page-hero-subtitle
                  @variant="inherit"
                  class="tio-hero-subtitle text-lg max-w-2xl"
                >
                  {{@subtitle}}
                </Text>
              {{/if}}
              {{#if (has-block "actions")}}
                <div data-test-page-hero-actions class="tio-row mt-4">
                  {{yield to="actions"}}
                </div>
              {{/if}}
            </div>
            {{#if (has-block "stat")}}
              <div data-test-page-hero-stat class="text-right tio-on-hero-tile rounded-lg p-4">
                {{yield to="stat"}}
              </div>
            {{/if}}
          </div>
          {{yield}}
          {{#if (has-block "content")}}
            <div data-test-page-hero-content class="mt-6">
              {{yield to="content"}}
            </div>
          {{/if}}
        {{else}}
          {{! Minimal mode: just default block }}
          {{yield}}
        {{/if}}
        {{#if (has-block "badge")}}
          <div
            data-test-page-hero-badge
            class="tio-wrap-tight mt-4 {{if this.isCentered 'justify-center'}}"
          >
            {{yield to="badge"}}
          </div>
        {{/if}}
        {{#if (has-block "footer")}}
          <div data-test-page-hero-footer class="tio-hero-footer">
            {{yield to="footer"}}
          </div>
        {{/if}}
      </div>
    </div>
  </template>
}
